import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Truck, Upload, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { AppLayout } from '../../../components/layout/AppLayout';
import { pickupAPI } from '../../../services/api';
import { toast } from 'sonner';

const RunnerRegistrationPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [existingApplication, setExistingApplication] = useState(null);
  
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    id_number: '',
    vehicle_type: 'car',
    vehicle_registration: '',
    license_number: '',
    availability: 'full_time'
  });

  const [documents, setDocuments] = useState({
    id_document: null,
    license_document: null,
    profile_photo: null
  });

  useEffect(() => {
    checkExistingApplication();
  }, []);

  const checkExistingApplication = async () => {
    setLoading(true);
    try {
      const { data } = await pickupAPI.getMyDriverApplication();
      if (data.application) {
        setExistingApplication(data.application);
      }
    } catch (error) {
      console.error('Check application error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e, docType) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setDocuments(prev => ({ ...prev, [docType]: file }));
    }
  };

  const handleSubmitApplication = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Submit application
      const { data } = await pickupAPI.submitDriverApplication(formData);
      const applicationId = data.application_id;

      toast.success('Application submitted successfully!');

      // Upload documents if any are selected
      if (documents.id_document || documents.license_document || documents.profile_photo) {
        await handleUploadDocuments(applicationId);
      }

      // Reload application status
      checkExistingApplication();
    } catch (error) {
      console.error('Submit application error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUploadDocuments = async (applicationId) => {
    setUploading(true);
    
    try {
      const formData = new FormData();
      
      if (documents.id_document) {
        formData.append('id_document', documents.id_document);
      }
      if (documents.license_document) {
        formData.append('license_document', documents.license_document);
      }
      if (documents.profile_photo) {
        formData.append('profile_photo', documents.profile_photo);
      }

      await pickupAPI.uploadDriverDocuments(
        applicationId || existingApplication.id,
        formData
      );

      toast.success('Documents uploaded successfully!');
      checkExistingApplication();
    } catch (error) {
      console.error('Upload documents error:', error);
      toast.error('Failed to upload documents');
    } finally {
      setUploading(false);
    }
  };

  const getStatusBadge = (status, verificationType) => {
    if (verificationType === 'verified') {
      return <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
        <CheckCircle className="h-3 w-3 mr-1" />
        Verified Runner
      </Badge>;
    }
    
    if (verificationType === 'unverified' && status === 'approved') {
      return <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
        <AlertCircle className="h-3 w-3 mr-1" />
        Unverified Runner (Limited)
      </Badge>;
    }

    if (status === 'pending') {
      return <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
        <Clock className="h-3 w-3 mr-1" />
        Under Review
      </Badge>;
    }

    if (status === 'rejected') {
      return <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
        <AlertCircle className="h-3 w-3 mr-1" />
        Rejected
      </Badge>;
    }

    return null;
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
        </div>
      </AppLayout>
    );
  }

  // If already has an application
  if (existingApplication) {
    return (
      <AppLayout>
        <div className="max-w-4xl mx-auto space-y-6 px-4 py-8">
          <div className="flex items-center gap-3">
            <Truck className="h-8 w-8 text-primary" />
            <div>
              <h1 className="text-3xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Runner Application Status
              </h1>
              <p className="text-muted-foreground mt-1">
                Track your runner registration progress
              </p>
            </div>
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Your Application</CardTitle>
                {getStatusBadge(existingApplication.status, existingApplication.verification_status)}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Full Name</Label>
                  <p className="font-medium">{existingApplication.full_name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Phone</Label>
                  <p className="font-medium">{existingApplication.phone}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Vehicle Type</Label>
                  <p className="font-medium capitalize">{existingApplication.vehicle_type || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Documents Status</Label>
                  <p className="font-medium">
                    {existingApplication.documents_uploaded ? '✅ Uploaded' : '❌ Not uploaded'}
                  </p>
                </div>
              </div>

              <div>
                <Label className="text-muted-foreground">Submitted</Label>
                <p className="font-medium">{new Date(existingApplication.submitted_at).toLocaleString()}</p>
              </div>

              {existingApplication.status === 'approved' && (
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <p className="text-green-800 dark:text-green-400 font-medium">
                    🎉 Congratulations! You are now a {existingApplication.verification_status} runner.
                  </p>
                  <p className="text-sm text-green-700 dark:text-green-500 mt-1">
                    {existingApplication.verification_status === 'verified'
                      ? 'You have full access to all delivery opportunities with no limits.'
                      : 'You can start accepting jobs (max 3 active). Upload documents to become a verified runner for unlimited access.'}
                  </p>
                  <Button 
                    className="mt-3" 
                    onClick={() => navigate('/pickup/my-jobs')}
                  >
                    Browse Available Deliveries
                  </Button>
                </div>
              )}

              {existingApplication.status === 'pending' && (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <p className="text-blue-800 dark:text-blue-400 font-medium">
                    ⏳ Your application is under review
                  </p>
                  <p className="text-sm text-blue-700 dark:text-blue-500 mt-1">
                    Our team will review your application within 1-2 business days. You'll be notified once approved.
                  </p>
                </div>
              )}

              {/* Upload documents section */}
              {existingApplication.status === 'approved' && !existingApplication.documents_uploaded && (
                <Card className="border-2 border-dashed">
                  <CardHeader>
                    <CardTitle className="text-lg">Upload Verification Documents</CardTitle>
                    <CardDescription>
                      Upload your documents to become a verified runner and unlock unlimited jobs
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label>Government-issued ID</Label>
                      <Input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => handleFileChange(e, 'id_document')}
                      />
                      {documents.id_document && (
                        <p className="text-sm text-green-600 mt-1">✓ {documents.id_document.name}</p>
                      )}
                    </div>

                    <div>
                      <Label>Runner's License/ID</Label>
                      <Input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => handleFileChange(e, 'license_document')}
                      />
                      {documents.license_document && (
                        <p className="text-sm text-green-600 mt-1">✓ {documents.license_document.name}</p>
                      )}
                    </div>

                    <div>
                      <Label>Profile Photo</Label>
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileChange(e, 'profile_photo')}
                      />
                      {documents.profile_photo && (
                        <p className="text-sm text-green-600 mt-1">✓ {documents.profile_photo.name}</p>
                      )}
                    </div>

                    <Button 
                      onClick={() => handleUploadDocuments(existingApplication.id)}
                      disabled={uploading || (!documents.id_document && !documents.license_document && !documents.profile_photo)}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {uploading ? 'Uploading...' : 'Upload Documents'}
                    </Button>
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  // New application form
  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto space-y-6 px-4 py-8">
        <div className="flex items-center gap-3">
          <Truck className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-3xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Become a Matrise Runner
            </h1>
            <p className="text-muted-foreground mt-1">
              Start earning by completing errands and jobs in your area
            </p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Runner Registration</CardTitle>
            <CardDescription>
              Fill out the form below to apply as a runner
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmitApplication} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Full Name *</Label>
                  <Input
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your full name"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Phone Number *</Label>
                  <Input
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    placeholder="+27..."
                  />
                </div>

                <div className="space-y-2">
                  <Label>ID Number</Label>
                  <Input
                    name="id_number"
                    value={formData.id_number}
                    onChange={handleInputChange}
                    placeholder="ID or Passport number"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Vehicle Type *</Label>
                  <select
                    name="vehicle_type"
                    value={formData.vehicle_type}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded-md"
                    required
                  >
                    <option value="car">Car</option>
                    <option value="van">Van</option>
                    <option value="truck">Truck</option>
                    <option value="motorcycle">Motorcycle</option>
                    <option value="bicycle">Bicycle</option>
                    <option value="public_transport">Public Transport</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label>Vehicle Registration</Label>
                  <Input
                    name="vehicle_registration"
                    value={formData.vehicle_registration}
                    onChange={handleInputChange}
                    placeholder="License plate number"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Runner's License (Optional)</Label>
                  <Input
                    name="license_number"
                    value={formData.license_number}
                    onChange={handleInputChange}
                    placeholder="Your license number"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label>Availability *</Label>
                  <select
                    name="availability"
                    value={formData.availability}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded-md"
                    required
                  >
                    <option value="full_time">Full Time</option>
                    <option value="part_time">Part Time</option>
                    <option value="weekends">Weekends Only</option>
                    <option value="flexible">Flexible</option>
                  </select>
                </div>
              </div>

              {/* Optional Document Upload */}
              <div className="border-t pt-4 space-y-4">
                <h3 className="font-medium">Upload Documents (Optional - can be done later)</h3>
                
                <div className="space-y-2">
                  <Label>Government-issued ID</Label>
                  <Input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => handleFileChange(e, 'id_document')}
                  />
                  {documents.id_document && (
                    <p className="text-sm text-green-600">✓ {documents.id_document.name}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>License/ID Document</Label>
                  <Input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => handleFileChange(e, 'license_document')}
                  />
                  {documents.license_document && (
                    <p className="text-sm text-green-600">✓ {documents.license_document.name}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Profile Photo</Label>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, 'profile_photo')}
                  />
                  {documents.profile_photo && (
                    <p className="text-sm text-green-600">✓ {documents.profile_photo.name}</p>
                  )}
                </div>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                <p className="text-sm text-blue-800 dark:text-blue-400">
                  <strong>Note:</strong> You can start accepting deliveries immediately after approval (max 3 active). 
                  Upload verification documents to become a verified runner with unlimited access.
                </p>
              </div>

              <Button type="submit" disabled={submitting} className="w-full">
                {submitting ? 'Submitting...' : 'Submit Application'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
};

export default RunnerRegistrationPage;
